/**
 * Scan Page.
 *
 * Dedicated page for barcode scanning. Scans a barcode, looks up the
 * asset by SKU, and navigates to the asset detail page. Accessible
 * from the mobile bottom nav.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, Package, AlertCircle } from "lucide-react";
import BarcodeScanner from "@/features/scanner/BarcodeScanner";
import { useAssets } from "@/features/inventory/api";
import Card from "@/components/ui/Card";
import Button from "@/components/ui/Button";

function ScanPage() {
  const navigate = useNavigate();
  const [scannedSku, setScannedSku] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Search for the scanned SKU.
  const { data: searchResults, isLoading } = useAssets({
    search: scannedSku || undefined,
    pageSize: 5,
  });

  const handleScan = (code: string) => {
    setScannedSku(code);
    setNotFound(false);
  };

  // Auto-navigate if exactly one result matches.
  if (scannedSku && searchResults && !isLoading) {
    const exactMatch = searchResults.items.find(
      (a) => a.sku === scannedSku || a.barcode === scannedSku,
    );
    if (exactMatch) {
      navigate(`/inventory/${exactMatch.id}`);
      return null;
    }
    if (searchResults.items.length === 0 && !notFound) {
      setNotFound(true);
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--fill-info)]">
          <ScanLine className="h-6 w-6 text-[var(--color-interactive-primary)]" />
        </div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Scan Asset
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Scan a barcode or enter a SKU to look up an asset.
        </p>
      </div>

      <Card>
        <BarcodeScanner onScan={handleScan} />
      </Card>

      {/* Loading */}
      {isLoading && scannedSku && (
        <Card>
          <p className="text-center text-sm text-[var(--text-secondary)]">
            Looking up "{scannedSku}"...
          </p>
        </Card>
      )}

      {/* Not Found */}
      {notFound && (
        <Card>
          <div className="flex flex-col items-center py-4">
            <AlertCircle className="h-8 w-8 text-yellow-500" />
            <p className="mt-2 text-sm font-medium text-[var(--text-primary)]">
              No asset found for "{scannedSku}"
            </p>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              Check the barcode and try again.
            </p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              onClick={() => {
                setScannedSku(null);
                setNotFound(false);
              }}
            >
              Scan Again
            </Button>
          </div>
        </Card>
      )}

      {/* Multiple results */}
      {scannedSku &&
        searchResults &&
        searchResults.items.length > 0 &&
        !searchResults.items.find((a) => a.sku === scannedSku) && (
          <Card>
            <h3 className="mb-3 text-sm font-medium text-[var(--text-primary)]">
              Possible matches for "{scannedSku}":
            </h3>
            <div className="space-y-2">
              {searchResults.items.map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => navigate(`/inventory/${asset.id}`)}
                  className="flex w-full items-center gap-3 rounded-[var(--radius-md)] border border-[var(--border-default)] p-3 text-left transition-colors hover:bg-[var(--surface-sunken)]"
                >
                  <Package className="h-5 w-5 text-[var(--text-tertiary)]" />
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">
                      {asset.name}
                    </p>
                    <p className="text-xs text-[var(--text-secondary)]">
                      {asset.sku}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        )}

      {/* Recent scans hint */}
      {!scannedSku && (
        <p className="text-center text-xs text-[var(--text-tertiary)]">
          Tip: Supports Code 128 barcodes and QR codes printed from asset
          labels.
        </p>
      )}
    </div>
  );
}

export default ScanPage;
